
import React from "react";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { exportLeadsToCSV } from "@/services/csv/exportService";
import { Lead } from "@/types/lead";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

// Helper function to download the CSV file
const downloadCSV = (csvContent: string, filename: string): void => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

interface CSVExportButtonProps {
  leads: Lead[];
  filename?: string;
  includeAllFields?: boolean;
  variant?: "default" | "outline" | "secondary" | "ghost" | "link";
  size?: "default" | "sm" | "lg" | "icon";
  className?: string;
  totalFilteredCount?: number;
  onFetchAllLeads?: () => Promise<Lead[]>;
}

const CSVExportButton: React.FC<CSVExportButtonProps> = ({
  leads,
  filename = "leads-export.csv",
  includeAllFields = false,
  variant = "outline",
  size = "sm",
  className,
  totalFilteredCount,
  onFetchAllLeads,
}) => {
  const { toast } = useToast();
  const [isExporting, setIsExporting] = React.useState(false);
  const [includeExtraFields, setIncludeExtraFields] = React.useState(includeAllFields);
  const [includeAllNotes, setIncludeAllNotes] = React.useState(true);
  const [isOpen, setIsOpen] = React.useState(false);
  
  const willFetchAll = !!onFetchAllLeads;
  const exportCount = willFetchAll ? (totalFilteredCount || leads.length) : leads.length;

  const handleExport = async () => {
    if (exportCount === 0) {
      toast({
        title: "No leads to export",
        description: "There are no leads available to export.",
      });
      return;
    }
    
    setIsExporting(true);
    try {
      let leadsToExport = leads;

      if (willFetchAll) {
        leadsToExport = await onFetchAllLeads();
        if (!leadsToExport.length) {
          throw new Error('No leads fetched');
        }
      }

      const csvContent = await exportLeadsToCSV(leadsToExport, includeExtraFields);
      
      if (!csvContent) {
        throw new Error('Failed to generate CSV content');
      }
      
      const date = new Date().toISOString().split('T')[0];
      const filenameWithDate = filename.replace('.csv', `-${date}.csv`);
      
      downloadCSV(csvContent, filenameWithDate);
      
      toast({
        title: "Export Successful",
        description: `${leadsToExport.length} leads exported successfully.`,
      });
      setIsOpen(false);
    } catch (error) {
      console.error('Error exporting leads:', error);
      toast({
        title: "Export Failed",
        description: "An error occurred while exporting leads. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };
  
  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={variant}
          size={size}
          className={className}
          disabled={isExporting || exportCount === 0}
        >
          <Download className="h-4 w-4 mr-2" />
          Export CSV
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80">
        <div className="space-y-4">
          <h4 className="font-medium text-sm">Export Options</h4>
          
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="includeExtraFields" 
              checked={includeExtraFields} 
              onCheckedChange={(checked) => setIncludeExtraFields(!!checked)} 
            />
            <Label htmlFor="includeExtraFields">Include additional fields</Label>
          </div>
          
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="includeAllNotes" 
              checked={includeAllNotes}
              onCheckedChange={(checked) => setIncludeAllNotes(!!checked)}
              defaultChecked
            />
            <Label htmlFor="includeAllNotes">Include all lead notes</Label>
          </div>
          
          <Button 
            className="w-full" 
            onClick={handleExport}
            disabled={isExporting || exportCount === 0}
          >
            {isExporting ? (
              <span className="flex items-center">
                <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-2" />
                {willFetchAll ? 'Fetching & Exporting...' : 'Exporting...'}
              </span>
            ) : (
              <>Export {willFetchAll ? `All ${exportCount}` : exportCount} Leads</>
            )}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default CSVExportButton;
